'use client';

import { useCallback, useEffect } from 'react';
import config from 'config';
import {
  useCancelAppointmentMutation,
  useGetSystemPreferencesQuery
} from 'services/userProfileApi';

import { Flex, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setCancelationAppointment } from 'store/reducers/bookingAppointmentSlice';

import {
  formatPhoneNumbers,
  getCancellationModalBodyParts,
  getCancellationModalSubtitle,
  getCancellationModalTitle,
  getCancellationType,
  isApiFlagEnabled,
  isWithinLateCancelWindow,
  resolveContactPhone
} from 'utils';

import { CancellationType } from 'types';

import { Button, Link, Modal, Text } from 'ui';

const {
  myAppointments: { contactPhone },
  contacts: { phone: contactsPhone }
} = config;

const CancelationAppointmentModal = () => {
  const {
    colors: {
      palette: {
        components: { simpleErrorModal }
      }
    }
  } = useTheme();

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { cancelationAppointment } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const [cancelAppointment] = useCancelAppointmentMutation();
  const { data: sysPrefs } = useGetSystemPreferencesQuery('');
  const phone = resolveContactPhone(sysPrefs, contactPhone || contactsPhone);

  const { allow_cancel, confirmation_num } = cancelationAppointment ?? {};
  const cancelLeadHours = user?.olb?.allow_cancel_hrs ?? 24;
  const globalCancelAllowed = isApiFlagEnabled(user?.olb?.allow_cancel);
  const appointmentCancelAllowed = isApiFlagEnabled(allow_cancel);
  const isLateCancel = cancelationAppointment
    ? isWithinLateCancelWindow(cancelationAppointment, cancelLeadHours)
    : false;

  const cancellationType = getCancellationType(
    globalCancelAllowed,
    isLateCancel,
    appointmentCancelAllowed
  );

  const title = getCancellationModalTitle(cancellationType);
  const subtitle = getCancellationModalSubtitle(cancellationType);
  const { prefix: bodyPrefix, suffix: bodySuffix } =
    getCancellationModalBodyParts(cancellationType);
  const formattedPhone = formatPhoneNumbers(phone);

  const onClose = useCallback(() => {
    dispatch(setCancelationAppointment(null));
  }, [dispatch]);

  const handleConfirmCancel = async () => {
    onClose();
    await cancelAppointment({ id: confirmation_num ?? 0 });
  };

  useEffect(
    () => () => {
      if (cancelationAppointment !== null) {
        onClose();
      }
    },
    [onClose, cancelationAppointment]
  );

  return (
    <Modal
      isOpen={cancelationAppointment !== null}
      onClose={onClose}
      size={{ base: 'xs', md: '2xl', lg: '2xl', '3xl': '6xl' }}
      isCentered
    >
      <Flex gap={0} flexDirection={{ base: 'column', md: 'row' }}>
        <VStack
          align='center'
          flex='1'
          gap={0}
          px={{ base: '16px', md: '48px', '2xl': '70px' }}
          py={{ base: '30px', md: '30px', lg: '40px', '2xl': '65px' }}
        >
          <Text
            fontSize={{ base: '26px', md: '32px', lg: '38px' }}
            color={simpleErrorModal.titleColor}
            textAlign='center'
            mb={{ base: '12px', md: '20px' }}
          >
            {title}
          </Text>
          <Text
            fontSize={{ base: '18px', md: '20px', lg: '26px' }}
            color={simpleErrorModal.descriptionColor}
            textAlign='center'
            mb={{ base: '12px', md: '16px' }}
          >
            {subtitle}
          </Text>
          <Text
            fontSize={{ base: '14px', md: '16px', lg: '20px' }}
            color={simpleErrorModal.descriptionColor}
            textAlign='center'
            lineHeight={1.3}
          >
            {bodyPrefix}
            {formattedPhone ? (
              <Link
                to={`tel:${formattedPhone}`}
                sx={{
                  display: 'inline',
                  fontSize: 'inherit',
                  color: 'inherit',
                  fontWeight: 'bold',
                  lineHeight: 'inherit',
                  verticalAlign: 'baseline',
                  _hover: { textDecoration: 'underline' }
                }}
              >
                {formattedPhone}
              </Link>
            ) : null}
            {bodySuffix}
          </Text>
          <Flex
            w='100%'
            justifyContent='center'
            alignItems='center'
            mt={{ base: '30px', sm: '40px', xl: '50px' }}
            gap={{
              base: '10px',
              md: '20px'
            }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            {cancellationType !== CancellationType.DENIED && (
              <Button
                w='224px'
                height='45px'
                variant='solid'
                colorScheme='red'
                fontSize='14px'
                fontWeight='bold'
                onClick={handleConfirmCancel}
              >
                Confirm Cancel
              </Button>
            )}
            <Button
              w='224px'
              height='45px'
              variant='outline'
              colorScheme='black'
              fontSize='14px'
              fontWeight='bold'
              onClick={onClose}
            >
              {cancellationType === CancellationType.DENIED
                ? 'Close'
                : 'OK, DO NOT CANCEL'}
            </Button>
          </Flex>
        </VStack>
      </Flex>
    </Modal>
  );
};

export default CancelationAppointmentModal;
